<template>
  <v-text-field ref="textField" class="brand-text-field" v-bind="mergedAttrs">
    <!-- Custom character counter -->
    <template v-if="showCustomCounter" #counter>
      <span class="text-caption" :class="counterColorClass">{{ counterText }}</span>
    </template>

    <!-- Forward all other parent slots -->
    <template v-for="(_, slotName) in forwardedSlots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-text-field>
</template>

<script setup>
import { computed, useAttrs, useSlots, ref, onMounted } from 'vue'
import { mergeAttrs } from '@/utils/vue-attrs'
import { useCharacterCounter } from '@/composables/useCharacterCounter'

// Define props
const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
  autoselect: Boolean,
  maxlength: {
    type: Number,
    default: null,
  },
})

// Get attrs and slots
const attrs = useAttrs()
const slots = useSlots()

// Template ref
const textField = ref(null)

// Character counter (always called unconditionally, safe when maxlength is null)
const currentLength = computed(() => (attrs.modelValue ?? attrs['model-value'] ?? '').toString().length)
const showCustomCounter = computed(() => props.maxlength != null && !slots.counter)
const { counterText, counterColorClass } = useCharacterCounter(currentLength, computed(() => props.maxlength))

// Filter out 'counter' slot from forwarding when custom counter is active
const forwardedSlots = computed(() => {
  if (!showCustomCounter.value) return slots
  const { counter, ...rest } = slots
  return rest
})

// Define variants
const VARIANTS = {
  basic: {
    variant: 'outlined',
    bg: 'neutral0',
    color: 'blue400',
    'base-color': 'neutral50',
    classes: ['brand-text-field--basic', 'rounded-lg'],
  },
  secondary: {
    variant: 'solo',
    flat: true,
    bg: 'neutral30',
    color: 'text',
    classes: ['brand-text-field--secondary', 'rounded-lg'],
  },
}

// Computed common attributes
const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = []
  const common = {
    'hide-details': 'auto',
    'validate-on': 'lazy',
  }

  // Activate Vuetify's counter infrastructure when maxlength is set
  if (props.maxlength != null) {
    common.counter = props.maxlength
  }

  return {
    ...common,
    ...variant,
    class: [
      ...classes,
      ...(variant?.classes ?? []),
    ],
  }
})

// Merge attributes properly
const mergedAttrs = computed(() => {
  return mergeAttrs(commonAttrs.value, attrs)
})

// Methods
const focus = () => {
  textField.value?.$el.querySelector('input')?.focus()
}

const blur = () => {
  textField.value?.$el.querySelector('input')?.blur()
}

const validate = () => {
  textField.value?.validate()
}

const select = () => {
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/select#notes
  focus()
  textField.value?.$el.querySelector('input')?.select()
}

// Expose methods for parent component access
defineExpose({
  focus,
  blur,
  validate,
  select,
})

// Set inheritAttrs to false
defineOptions({
  inheritAttrs: false,
})

// Auto-select on mount if needed
onMounted(() => {
  // same as autofocus but also selects the entire content
  if (props.autoselect) select()
})
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>

<style lang="scss" scoped>
.v-text-field.brand-text-field {
  &.brand-text-field--basic {
    &:not(.v-input--disabled) {
      // Use theme-aware border colors
      :deep(.v-field__outline) {
        // Light mode: subtle gray border
        color: rgba(0, 0, 0, 0.15);
      }

      // Adjust border radius to match design
      :deep(.v-field) {
        border-radius: 12px !important;
      }

      // Ensure proper field density to match Vue 2 sizing
      :deep(.v-field__input) {
        min-height: 48px;
        padding-top: 14px;
        padding-bottom: 14px;
      }
    }

    &.v-input--density-compact :deep(.v-input__append-inner) {
      margin-top: 6px;
    }

    &.v-input--density-compact :deep(.v-input__prepend-inner) {
      margin-top: 12px;
    }
  }
}
</style>

<style lang="scss">
// Dark mode specific styles - unscoped to ensure proper cascade
.v-theme--dark {
  .v-text-field.brand-text-field.brand-text-field--basic:not(.v-input--disabled) {
    .v-field__outline {
      color: rgb(var(--v-theme-neutral50)) !important;
    }
  }
}
</style>
